import React, { useState } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
  useLocation,
} from "react-router-dom";
import { QueryClient, QueryClientProvider } from "react-query";
import { Toaster } from "react-hot-toast";
import Dashboard from "./pages/Dashboard";
import Login from "./pages/Login";
import Register from "./pages/Register";
import VerifyMagicLink from "./pages/VerifyMagicLink";
import ExpiryItems from "./pages/ExpiryItems";
import ArchivedItems from "./pages/ArchivedItems";
import LandingPage from "./pages/LandingPage";
import TeamSettings from "./pages/TeamSettings";
import AcceptInvite from "./pages/AcceptInvite";
import SharedItemView from "./pages/SharedItemView";
import AuditTrailPage from "./pages/AuditTrailPage";
import PrivacyPolicy from "./pages/PrivacyPolicy";
import TermsOfService from "./pages/TermsOfService";
import CookiePolicy from "./pages/CookiePolicy";
import PricingPage from "./pages/PricingPage";
import RefundPolicy from "./pages/RefundPolicy";
import BillingSuccessPage from "./pages/BillingSuccessPage";
import BillingCanceledPage from "./pages/BillingCanceledPage";
import Navbar from "./components/Navbar";
import { AuthProvider, useAuth } from "./hooks/useAuth";
import ChatWidget from "./components/ChatWidget";
import "./App.css";

// ─── Beta announcement banner ──────────────────────────────────────────────────
const BANNER_KEY = "sentrilog_beta_banner_v1";

const BetaBanner: React.FC = () => {
  const [visible, setVisible] = useState<boolean>(
    () => localStorage.getItem(BANNER_KEY) !== "dismissed"
  );
  const [modalOpen, setModalOpen] = useState(false);
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState<string | null>(null);
  const [formError, setFormError] = useState<string | null>(null);

  const dismiss = () => {
    localStorage.setItem(BANNER_KEY, "dismissed");
    setVisible(false);
    setModalOpen(false);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim()) return;
    setSubmitting(true);
    setFormError(null);
    try {
      const res = await fetch("/api/early-bird", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: email.trim(), source: "beta_banner" }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "Something went wrong");
      setDone(data.message);
    } catch (err: any) {
      setFormError(err.message || "Failed to submit. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  if (!visible) return null;

  return (
    <>
      {/* ── Sticky top banner ─────────────────────────────────────────── */}
      <div className="relative z-[70] bg-gradient-to-r from-[#1D4ED8] via-[#2563EB] to-[#7C3AED] text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between gap-3 py-2.5">
            {/* Left — pulse + message */}
            <div className="flex items-center gap-2.5 min-w-0">
              <span className="relative flex-shrink-0 h-2.5 w-2.5">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-yellow-300 opacity-75" />
                <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-yellow-300" />
              </span>
              <p className="text-sm font-medium truncate">
                <span className="font-bold">🚀 Beta</span>
                {" — "}
                Sentrilog is in early testing. We&apos;re onboarding first users
                now.
              </p>
            </div>

            {/* Centre — CTA */}
            <button
              onClick={() => setModalOpen(true)}
              className="flex-shrink-0 inline-flex items-center gap-1.5 px-3.5 py-1.5
                         bg-yellow-300 hover:bg-yellow-200 text-[#1D4ED8] text-xs font-bold
                         rounded-full transition-colors shadow-sm whitespace-nowrap"
            >
              🎁 Get Early Bird Access
              <svg
                className="h-3 w-3"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2.5}
                  d="M17 8l4 4m0 0l-4 4m4-4H3"
                />
              </svg>
            </button>

            {/* Right — dismiss */}
            <button
              onClick={dismiss}
              aria-label="Dismiss beta banner"
              className="flex-shrink-0 p-1 rounded-md text-white/70 hover:text-white hover:bg-white/15 transition-colors"
            >
              <svg
                className="h-4 w-4"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          </div>
        </div>
      </div>

      {/* ── Early-bird signup modal ────────────────────────────────────── */}
      {modalOpen && (
        <div className="fixed inset-0 z-[80] flex items-center justify-center p-4">
          {/* Backdrop */}
          <div
            className="absolute inset-0 bg-black/50 backdrop-blur-sm"
            onClick={() => !submitting && setModalOpen(false)}
          />

          {/* Card */}
          <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-md p-8 text-center">
            {/* Close button */}
            {!submitting && (
              <button
                onClick={() => setModalOpen(false)}
                className="absolute top-4 right-4 p-1.5 rounded-md text-[#9CA3AF]
                           hover:text-[#374151] hover:bg-[#F3F4F6] transition-colors"
              >
                <svg
                  className="h-4 w-4"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            )}

            {done ? (
              /* ── Success ─────────────────────────────────────────────── */
              <>
                <div className="h-16 w-16 bg-[#F0FDF4] rounded-full flex items-center justify-center mx-auto mb-4">
                  <svg
                    className="h-8 w-8 text-[#16A34A]"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M5 13l4 4L19 7"
                    />
                  </svg>
                </div>
                <h3 className="text-xl font-bold text-[#111827] mb-2">
                  You&apos;re on the list!
                </h3>
                <p className="text-[#6B7280] text-sm mb-6">{done}</p>
                <button
                  onClick={dismiss}
                  className="w-full py-2.5 bg-[#2563EB] hover:bg-[#1D4ED8] text-white
                             text-sm font-semibold rounded-xl transition-colors"
                >
                  Got it, thanks! 🎉
                </button>
              </>
            ) : (
              /* ── Form ────────────────────────────────────────────────── */
              <>
                <div className="h-16 w-16 bg-gradient-to-br from-[#DBEAFE] to-[#EDE9FE] rounded-2xl flex items-center justify-center mx-auto mb-5">
                  <span className="text-3xl">🎁</span>
                </div>

                <h3 className="text-xl font-bold text-[#111827] mb-1">
                  Get Early Bird Access
                </h3>
                <p className="text-[#6B7280] text-sm mb-6 leading-relaxed">
                  Be the first to know when Sentrilog launches publicly. Early
                  birds get{" "}
                  <span className="font-semibold text-[#2563EB]">
                    exclusive pricing
                  </span>{" "}
                  and priority onboarding.
                </p>

                <form onSubmit={handleSubmit} className="space-y-3">
                  <input
                    type="email"
                    required
                    autoFocus
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="you@company.com"
                    className="w-full px-4 py-3 border border-[#E5E7EB] rounded-xl text-sm
                               text-[#111827] placeholder:text-[#9CA3AF]
                               focus:outline-none focus:ring-2 focus:ring-[#2563EB]
                               focus:border-transparent"
                  />

                  {formError && (
                    <p className="text-xs text-[#DC2626] bg-[#FEF2F2] border border-[#FECACA] rounded-lg px-3 py-2 text-left">
                      {formError}
                    </p>
                  )}

                  <button
                    type="submit"
                    disabled={submitting || !email.trim()}
                    className="w-full py-3 bg-[#2563EB] hover:bg-[#1D4ED8] disabled:opacity-50
                               disabled:cursor-not-allowed text-white text-sm font-semibold
                               rounded-xl transition-colors flex items-center justify-center gap-2"
                  >
                    {submitting ? (
                      <>
                        <svg
                          className="animate-spin h-4 w-4"
                          fill="none"
                          viewBox="0 0 24 24"
                        >
                          <circle
                            className="opacity-25"
                            cx="12"
                            cy="12"
                            r="10"
                            stroke="currentColor"
                            strokeWidth="4"
                          />
                          <path
                            className="opacity-75"
                            fill="currentColor"
                            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
                          />
                        </svg>
                        Saving…
                      </>
                    ) : (
                      "Count me in 🚀"
                    )}
                  </button>
                </form>

                <p className="text-[10px] text-[#9CA3AF] mt-4">
                  No spam, ever. Unsubscribe anytime.
                </p>
              </>
            )}
          </div>
        </div>
      )}
    </>
  );
};

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: 1,
    },
  },
});

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <Router>
          <div className="min-h-screen bg-app">
            <Toaster position="top-right" />
            <Routes>
              {/* Public routes */}
              <Route path="/" element={<LandingPage />} />
              <Route path="/login" element={<Login />} />
              <Route path="/register" element={<Register />} />
              <Route path="/auth/verify" element={<VerifyMagicLink />} />
              {/* Team invite acceptance — public (user may not be logged in yet) */}
              <Route path="/team/accept" element={<AcceptInvite />} />
              {/* Legal & marketing pages — public */}
              <Route path="/privacy" element={<PrivacyPolicy />} />
              <Route path="/terms" element={<TermsOfService />} />
              <Route path="/cookie-policy" element={<CookiePolicy />} />
              <Route path="/refund" element={<RefundPolicy />} />
              <Route path="/pricing" element={<PricingPage />} />
              {/* Option 4 — Magic link read-only item view for external stakeholders */}
              <Route path="/shared/:token" element={<SharedItemView />} />

              {/* Protected routes */}
              <Route
                path="/dashboard"
                element={
                  <ProtectedRoute>
                    <Dashboard />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/items"
                element={
                  <ProtectedRoute>
                    <ExpiryItems />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/archived"
                element={
                  <ProtectedRoute>
                    <ArchivedItems />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/team"
                element={
                  <ProtectedRoute>
                    <TeamSettings />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/audit"
                element={
                  <ProtectedRoute>
                    <AuditTrailPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/billing/success"
                element={
                  <ProtectedRoute>
                    <BillingSuccessPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/billing/canceled"
                element={
                  <ProtectedRoute>
                    <BillingCanceledPage />
                  </ProtectedRoute>
                }
              />
            </Routes>
          </div>
        </Router>
      </AuthProvider>
    </QueryClientProvider>
  );
}

function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  // Dashboard ("/dashboard") and ExpiryItems ("/items") both have their own Header —
  // only show the legacy Navbar on any other routes.
  const pagesWithOwnHeader = [
    "/dashboard",
    "/items",
    "/archived",
    "/team",
    "/audit",
  ];
  const showNavbar = !pagesWithOwnHeader.includes(location.pathname);

  return (
    <>
      <BetaBanner />
      {showNavbar && <Navbar />}
      {children}
      <ChatWidget />
    </>
  );
}

export default App;
